import { brandConfig, features } from '@/config/brands'
import { createDebugger } from '@/utils/debug'

const debug = createDebugger('ad')

const SCROLL_SLOTS = [
  { name: 'detail1', unitPath: '/23357064594/iaa-scroll.storyella.top_1072/iaa-scroll.storyella.top_1072_detail1' },
  { name: 'detail2', unitPath: '/23357064594/iaa-scroll.storyella.top_1072/iaa-scroll.storyella.top_1072_detail2' },
  { name: 'detail3', unitPath: '/23357064594/iaa-scroll.storyella.top_1072/iaa-scroll.storyella.top_1072_detail3' },
  { name: 'detail4', unitPath: '/23357064594/iaa-scroll.storyella.top_1072/iaa-scroll.storyella.top_1072_detail4' },
  { name: 'detail5', unitPath: '/23357064594/iaa-scroll.storyella.top_1072/iaa-scroll.storyella.top_1072_detail5' },
  { name: 'detail6', unitPath: '/23357064594/iaa-scroll.storyella.top_1072/iaa-scroll.storyella.top_1072_detail6' },
  { name: 'detail7', unitPath: '/23357064594/iaa-scroll.storyella.top_1072/iaa-scroll.storyella.top_1072_detail7' },
  { name: 'detail8', unitPath: '/23357064594/iaa-scroll.storyella.top_1072/iaa-scroll.storyella.top_1072_detail8' },
  { name: 'detail9', unitPath: '/23357064594/iaa-scroll.storyella.top_1072/iaa-scroll.storyella.top_1072_detail9' },
  { name: 'detail10', unitPath: '/23357064594/iaa-scroll.storyella.top_1072/iaa-scroll.storyella.top_1072_detail10' },
]

const NON_SCROLL_SLOTS = [
  { name: 'detail1', unitPath: '/23357064594/iaa.storyella.top_1072/iaa.storyella.top_1072_detail1' },
  { name: 'detail2', unitPath: '/23357064594/iaa.storyella.top_1072/iaa.storyella.top_1072_detail2' },
  { name: 'detail3', unitPath: '/23357064594/iaa.storyella.top_1072/iaa.storyella.top_1072_detail3' },
  { name: 'detail4', unitPath: '/23357064594/iaa.storyella.top_1072/iaa.storyella.top_1072_detail4' },
  { name: 'detail5', unitPath: '/23357064594/iaa.storyella.top_1072/iaa.storyella.top_1072_detail5' },
  { name: 'detail6', unitPath: '/23357064594/iaa.storyella.top_1072/iaa.storyella.top_1072_detail6' },
]

const SIZE = [300, 250] as const
const AD_HOST_KEYWORD = brandConfig.texts?.adSenseHostKeyword || ''
const isScrollDomain = !AD_HOST_KEYWORD || AD_HOST_KEYWORD.split(',').map(k => k.trim()).some(k => location.hostname.includes(k))

const GPT_AD_SLOTS = isScrollDomain ? SCROLL_SLOTS : NON_SCROLL_SLOTS
const SLOT_COUNT = GPT_AD_SLOTS.length
const PRELOAD_BATCH_SIZE = SLOT_COUNT
const AD_ENABLED = !!(features?.enableAdSense && brandConfig.texts?.adSenseCharThreshold)

debug('域名%s关键字，使用%s广告位 (%d个)', isScrollDomain ? '匹配' : '不匹配', isScrollDomain ? 'scroll' : '非scroll', SLOT_COUNT)

let gptInited = false
const slotMap = new Map<string, GoogletagSlot>()
let nextPreloadOrder = 1
let allowedPreloadEnd = PRELOAD_BATCH_SIZE
const preloadWaiters: Array<{ order: number; resolve: () => void }> = []

function getGpt(): Googletag {
  return window.googletag as Googletag
}

function ensureGpt() {
  if (gptInited)
    return
  gptInited = true
  window.googletag = window.googletag || { cmd: [] }
  getGpt().cmd.push(() => {
    getGpt().pubads().enableSingleRequest()
    getGpt().pubads().enableAsyncRendering()
    getGpt().enableServices()
    debug('GPT 初始化完成')
  })
}

export function useGptAdManager() {
  const threshold = Number(brandConfig.texts?.adSenseCharThreshold) || 500

  function getAdSlotForIndex(index: number) {
    return GPT_AD_SLOTS[index % SLOT_COUNT]
  }

  function registerPreloadItem() {
    return nextPreloadOrder++
  }

  function waitForPreloadTurn(order: number): Promise<void> {
    if (order <= allowedPreloadEnd)
      return Promise.resolve()

    debug('等待预加载批次 globalOrder=%s batch=%s allowedEnd=%s', order, Math.ceil(order / PRELOAD_BATCH_SIZE), allowedPreloadEnd)

    return new Promise((resolve) => {
      preloadWaiters.push({ order, resolve })
    })
  }

  function releaseNextPreloadBatch(order: number) {
    if (order % PRELOAD_BATCH_SIZE !== 0)
      return

    const nextAllowedEnd = order + PRELOAD_BATCH_SIZE
    if (nextAllowedEnd <= allowedPreloadEnd)
      return

    allowedPreloadEnd = nextAllowedEnd
    debug('释放下一批预加载 triggerGlobalOrder=%s nextAllowedEnd=%s', order, allowedPreloadEnd)

    for (let i = preloadWaiters.length - 1; i >= 0; i--) {
      const waiter = preloadWaiters[i]
      if (waiter.order > allowedPreloadEnd)
        continue
      preloadWaiters.splice(i, 1)
      waiter.resolve()
    }
  }

  function displayAd(divId: string, unitPath: string, slotName = ''): Promise<boolean> {
    if (!AD_ENABLED)
      return Promise.resolve(false)

    ensureGpt()

    debug('预加载开始 slot=%s div=%s', slotName, divId)

    return new Promise((resolve) => {
      let done = false
      let handler: ((event: any) => void) | null = null
      let timer: ReturnType<typeof setTimeout>

      const finish = (filled: boolean) => {
        if (done)
          return
        done = true
        clearTimeout(timer)
        if (handler)
          getGpt().pubads().removeEventListener('slotRenderEnded', handler)
        resolve(filled)
      }

      timer = setTimeout(() => {
        if (!done) {
          debug.warn('超时 (8s) 未填充 slot=%s div=%s', slotName, divId)
          finish(false)
        }
      }, 8000)

      getGpt().cmd.push(() => {
        const slot = getGpt().defineSlot(unitPath, [...SIZE], divId)
        if (!slot) {
          if (!done) {
            debug.warn('defineSlot 失败 slot=%s div=%s', slotName, divId)
            finish(false)
          }
          return
        }
        slotMap.set(divId, slot)
        slot.addService(getGpt().pubads())

        handler = (event: any) => {
          if (event.slot.getSlotElementId() === divId && !done) {
            const filled = !event.isEmpty
            if (filled)
              debug('填充成功 slot=%s div=%s', slotName, divId)
            else
              debug.warn('填充失败（空广告） slot=%s div=%s', slotName, divId)
            finish(filled)
          }
        }
        getGpt().pubads().addEventListener('slotRenderEnded', handler)

        getGpt().display(divId)
      })
    })
  }

  function destroyAd(divId: string) {
    const slot = slotMap.get(divId)
    if (!slot || !window.googletag)
      return

    getGpt().cmd.push(() => {
      getGpt().destroySlots([slot])
      slotMap.delete(divId)
    })
  }

  function resetGptAdState() {
    preloadWaiters.splice(0).forEach(w => w.resolve())
    slotMap.clear()
    nextPreloadOrder = 1
    allowedPreloadEnd = PRELOAD_BATCH_SIZE
    debug('广告状态已重置')
  }

  return {
    getAdSlotForIndex,
    registerPreloadItem,
    waitForPreloadTurn,
    releaseNextPreloadBatch,
    displayAd,
    destroyAd,
    resetGptAdState,
    AD_ENABLED,
    threshold,
    SLOT_COUNT,
    isScrollDomain,
    PRELOAD_BATCH_SIZE,
  }
}
